import { useEffect, useState } from 'react';
import {
  getBankSyncSettings,
  saveBankSyncSettings,
  testBudgetBakersConnection,
  type BudgetBakersAccountPreview,
} from '../bancos/bancosApi';

const DEFAULT_POLL_INTERVAL_MINUTES = 60;

const labelClass = 'block text-foreground mb-2';
const labelStyle = {
  fontSize: 'var(--text-caption)',
  fontWeight: 'var(--font-weight-semi-bold)',
} as const;

const inputClass =
  'w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary';
const inputStyle = {
  borderRadius: 'var(--radius-button)',
  fontSize: 'var(--text-base)',
} as const;

/** ISO vindo da API para o yyyy-MM-dd que o campo de data usa. */
function toDateInput(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function formatDateTime(value: string | null): string {
  if (!value) return 'nunca';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'nunca';
  return date.toLocaleString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function BankSyncSettingsTab() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [hasApiToken, setHasApiToken] = useState(false);
  const [apiToken, setApiToken] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [historyStartDate, setHistoryStartDate] = useState('');
  const [pollIntervalMinutes, setPollIntervalMinutes] = useState<number>(
    DEFAULT_POLL_INTERVAL_MINUTES,
  );
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [lastSyncError, setLastSyncError] = useState<string | null>(null);

  const [testError, setTestError] = useState<string | null>(null);
  const [testAccounts, setTestAccounts] = useState<BudgetBakersAccountPreview[] | null>(
    null,
  );

  useEffect(() => {
    setIsLoading(true);
    setLoadError(null);
    getBankSyncSettings()
      .then((data) => {
        setHasApiToken(data.hasApiToken);
        setEnabled(data.enabled);
        setHistoryStartDate(toDateInput(data.historyStartDate));
        setPollIntervalMinutes(
          data.pollIntervalMinutes > 0
            ? data.pollIntervalMinutes
            : DEFAULT_POLL_INTERVAL_MINUTES,
        );
        setLastSyncedAt(data.lastSyncedAt);
        setLastSyncError(data.lastSyncError);
      })
      .catch(() => setLoadError('Não foi possível carregar as definições de bancos.'))
      .finally(() => setIsLoading(false));
  }, []);

  const handleTest = async () => {
    setIsTesting(true);
    setTestError(null);
    setTestAccounts(null);

    try {
      const result = await testBudgetBakersConnection(apiToken.trim() || null);

      if (result.succeeded) {
        setTestAccounts(result.accounts);
      } else {
        setTestError(result.error ?? 'A ligação falhou.');
      }
    } catch {
      setTestError('Não foi possível falar com o BudgetBakers.');
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    setSaveError(null);
    setSaveSuccess(false);

    if (
      !Number.isFinite(pollIntervalMinutes) ||
      pollIntervalMinutes < 15 ||
      pollIntervalMinutes > 1440
    ) {
      setSaveError('O intervalo tem de estar entre 15 minutos e 24 horas (1440 minutos).');
      return;
    }

    // Guardar com a sincronização ligada e sem token é permitido: fica à espera do token em vez
    // de recusar a gravação, e o ecrã Bancos diz em letra grande que a ligação falta configurar.

    setIsSaving(true);
    try {
      const updated = await saveBankSyncSettings({
        apiToken: apiToken.trim() === '' ? null : apiToken.trim(),
        enabled,
        historyStartDate:
          historyStartDate === '' ? null : new Date(`${historyStartDate}T00:00:00Z`).toISOString(),
        pollIntervalMinutes,
      });

      setHasApiToken(updated.hasApiToken);
      setApiToken('');
      setEnabled(updated.enabled);
      setHistoryStartDate(toDateInput(updated.historyStartDate));
      setPollIntervalMinutes(updated.pollIntervalMinutes);
      setLastSyncedAt(updated.lastSyncedAt);
      setLastSyncError(updated.lastSyncError);
      setSaveSuccess(true);
    } catch {
      setSaveError('Não foi possível guardar as definições.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div
        className="text-secondary-text py-8 text-center"
        style={{ fontSize: 'var(--text-base)' }}
      >
        A carregar definições...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="text-destructive py-4" style={{ fontSize: 'var(--text-base)' }}>
        {loadError}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p
        className="text-secondary-text"
        style={{ fontSize: 'var(--text-caption)', lineHeight: '1.5' }}
      >
        Ligação ao BudgetBakers, de onde vêm as transações bancárias. O token é gerado na
        conta do Wallet e tem validade limitada: quando expirar, a sincronização pára e o
        erro aparece aqui e no ecrã Bancos.
      </p>

      {/* Token */}
      <div>
        <label htmlFor="bank-sync-token" className={labelClass} style={labelStyle}>
          Token de acesso
        </label>
        <input
          id="bank-sync-token"
          type="password"
          value={apiToken}
          onChange={(event) => setApiToken(event.target.value)}
          placeholder={hasApiToken ? 'Guardado. Escreve aqui para substituir.' : 'Cola aqui o token'}
          // "new-password" e não "off": o Chrome ignora o "off" em campos de password e
          // preenchia aqui a palavra-passe guardada do site, que depois seria gravada como token.
          autoComplete="new-password"
          className={inputClass}
          style={inputStyle}
        />
        <p className="mt-1 text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
          {hasApiToken
            ? 'Há um token guardado. Deixa o campo vazio para o manter.'
            : 'Sem token, não há sincronização.'}
        </p>

        <button
          type="button"
          onClick={() => void handleTest()}
          disabled={isTesting || (!hasApiToken && apiToken.trim() === '')}
          className="mt-2 px-3 py-2 border border-border bg-background text-foreground transition-colors disabled:opacity-60"
          style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-caption)' }}
        >
          {isTesting ? 'A testar…' : 'Testar ligação'}
        </button>

        <div aria-live="polite">
          {testAccounts !== null && (
            <div className="mt-2 text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
              {testAccounts.length === 0
                ? 'Ligação boa, mas esta conta do BudgetBakers não tem contas bancárias.'
                : `Ligação boa. ${testAccounts.length} conta(s): ${testAccounts
                    .map((account) => account.name)
                    .join(', ')}.`}
            </div>
          )}
        </div>

        {testError && (
          <div className="mt-2 text-destructive" style={{ fontSize: 'var(--text-caption)' }} role="alert">
            {testError}
          </div>
        )}
      </div>

      {/* Sincronização automática */}
      <div>
        <label htmlFor="bank-sync-enabled" className={labelClass} style={labelStyle}>
          Sincronização automática
        </label>
        <div className="flex items-center gap-2">
          <input
            id="bank-sync-enabled"
            type="checkbox"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          <span style={{ fontSize: 'var(--text-base)' }}>
            Trazer as transações novas de tempos a tempos
          </span>
        </div>
        <p className="mt-1 text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
          Vem ligada. Desligada, as transações só chegam a quem carregar em Sincronizar agora no
          ecrã Bancos. Sem token guardado não sincroniza de qualquer maneira.
        </p>
      </div>

      {/* Intervalo */}
      <div>
        <label htmlFor="bank-sync-interval" className={labelClass} style={labelStyle}>
          Intervalo (minutos)
        </label>
        <input
          id="bank-sync-interval"
          type="number"
          value={pollIntervalMinutes}
          onChange={(event) => setPollIntervalMinutes(parseInt(event.target.value, 10))}
          min={15}
          max={1440}
          step={15}
          className={inputClass}
          style={{ ...inputStyle, maxWidth: 200 }}
        />
        <p className="mt-1 text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
          Default: 60. O banco não actualiza ao minuto, e a API só aceita 300 pedidos por hora.
        </p>
      </div>

      {/* Início do histórico */}
      <div>
        <label htmlFor="bank-sync-history-start" className={labelClass} style={labelStyle}>
          Início do histórico
        </label>
        <input
          id="bank-sync-history-start"
          type="date"
          value={historyStartDate}
          onChange={(event) => setHistoryStartDate(event.target.value)}
          className={inputClass}
          style={{ ...inputStyle, maxWidth: 200 }}
        />
        <p className="mt-1 text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
          A partir de que data se trazem transações. Recuar esta data obriga a uma
          sincronização completa para ir buscar o que ficou para trás.
        </p>
      </div>

      {/* Estado */}
      <div
        className="border border-border bg-muted px-4 py-3"
        style={{ borderRadius: 'var(--radius)', fontSize: 'var(--text-caption)' }}
      >
        <div>Última sincronização: {formatDateTime(lastSyncedAt)}.</div>
        {lastSyncError && (
          <div className="text-destructive mt-1">Último erro: {lastSyncError}</div>
        )}
      </div>

      <div aria-live="polite">
        {isSaving && (
          <span className="text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
            A guardar…
          </span>
        )}
        {saveSuccess && !isSaving && (
          <span className="text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
            Guardado.
          </span>
        )}
      </div>

      {saveError && (
        <div className="text-destructive" style={{ fontSize: 'var(--text-caption)' }} role="alert">
          {saveError}
        </div>
      )}

      <div>
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={isSaving}
          className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors disabled:opacity-60"
          style={{ borderRadius: 'var(--radius-button)' }}
        >
          {isSaving ? 'A guardar...' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}
